/**
 * Permission utility module
 * Handles permission checking with wildcard support
 */

/**
 * Checks if a user has a specific permission
 * Supports wildcard patterns:
 * - "*" grants all permissions
 * - "map.*" grants all map permissions
 * - "map.create" grants only map.create permission
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermission - The permission to check for
 * @returns True if the user has the required permission
 */
export function hasPermission(userPermissions: string[], requiredPermission: string): boolean {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  // Check for global wildcard
  if (userPermissions.includes('*')) {
    return true;
  }

  // Check for exact match
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }

  // Check for wildcard patterns (e.g., "map.*" matches "map.create")
  for (const userPerm of userPermissions) {
    if (userPerm.endsWith('.*')) {
      const prefix = userPerm.slice(0, -2);
      if (requiredPermission.startsWith(prefix + '.')) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks if a user has any of the specified permissions
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermissions - Array of permissions to check (OR logic)
 * @returns True if the user has at least one of the required permissions
 */
export function hasAnyPermission(userPermissions: string[], requiredPermissions: string[]): boolean {
  return requiredPermissions.some((perm) => hasPermission(userPermissions, perm));
}

/**
 * Checks if a user has all of the specified permissions
 * @param userPermissions - Array of permissions the user has
 * @param requiredPermissions - Array of permissions to check (AND logic)
 * @returns True if the user has all of the required permissions
 */
export function hasAllPermissions(userPermissions: string[], requiredPermissions: string[]): boolean {
  return requiredPermissions.every((perm) => hasPermission(userPermissions, perm));
}

/**
 * Permission constants for the application
 */
export const PERMISSIONS = {
  MAP_CREATE: 'map.create',
  MAP_EDIT: 'map.edit',
  MAP_DELETE: 'map.delete',
  MAP_ALL: 'map.*',
  FOLDER_CREATE: 'folder.create',
  FOLDER_DELETE: 'folder.delete',
  FOLDER_ALL: 'folder.*',
  ADMIN: '*'
} as const;
